import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { fasCheck } from 'shared/@generated/icons/fasCheck';
import { View } from 'react-native';
import { Props, PetInsuranceQuote, Listing, Animal } from 'shared/@types';
import { Text } from 'shared/components/text';
import { usePartners } from 'shared/library/partners';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';
import { Loader } from 'shared/components/loader';
import { analytics } from 'shared/library/analytics';
import { BookInsuranceCallModal } from 'shared/components/book-insurance-call-modal';
import { PetInsurancePriceModal } from 'shared/components/pet-insurance-price-modal';
import { useInViewOnce } from 'shared/library/use-in-view';
import { getPetInsuranceWidgetAnalyticsParams } from 'shared/components/pet-insurance-widget-analytics';
import { useCurrentRoute } from 'shared/library/routes/route';
import { preloadModal } from 'shared/components/lazy-modal.web';
import { preloadInsuranceCalculator } from 'shared/components/pet-insurance-calculator';
import { preloadInsuranceCall } from 'shared/components/lazy-book-insurance-call';

export const PetInsuranceWidget = ({ listing }: Props<{ listing: Listing }>) => {
  // Reset the impression and quote when client-side navigation changes the listing.
  return <PetInsuranceWidgetContent key={listing.id} listing={listing} />;
};

function PetInsuranceWidgetContent({ listing }: Props<{ listing: Listing }>) {
  const { insurancePartner } = usePartners();
  const currentRoute = useCurrentRoute();
  const { request } = useApi({ notifyOnServerErrors: false });
  const [quote, setQuote] = useState<PetInsuranceQuote | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const activeRequest = useRef<AbortController | null>(null);
  const widgetAnalyticsParams = useMemo(
    () => getPetInsuranceWidgetAnalyticsParams(listing, insurancePartner.name, currentRoute.name),
    [
      currentRoute.name,
      insurancePartner.name,
      listing.animal,
      listing.breed_id,
      listing.breed_name,
      listing.id,
    ],
  );
  const widgetRef = useInViewOnce(
    useCallback(() => {
      analytics.event('widget_impression', widgetAnalyticsParams);
      void Promise.all([
        preloadModal(),
        preloadInsuranceCalculator(),
        preloadInsuranceCall(),
      ]).catch(() => {
        // The modal loaders can retry if a visible widget's preload fails.
      });

      const controller = new AbortController();
      activeRequest.current = controller;
      request<{ quote: PetInsuranceQuote }>(
        apiRoute('pet-insurances.calculate-price-from-listing', { listing: listing.slug }),
        undefined,
        undefined,
        controller,
      )
        .then((res) => {
          if (!controller.signal.aborted) setQuote(res.quote);
        })
        .catch(() => {
          if (!controller.signal.aborted) setHasError(true);
        });
    }, [listing.slug, request, widgetAnalyticsParams]),
  );

  useEffect(() => () => activeRequest.current?.abort(), []);

  const features =
    listing.animal === Animal.Dog
      ? [
          'Hundförsäkring Trippel - topprankad av Konsumenternas Försäkringsbyrå!',
          'Flexibel försäkring. Välj omfattning och självrisk efter plånbok och behov.',
          'Digitala veterinärbesök via FirstVet ingår utan extra kostnad.',
        ]
      : [
          'Flexibel försäkring. Välj omfattning och självrisk efter plånbok och behov.',
          'Digitala veterinärbesök via FirstVet ingår utan extra kostnad.',
          'Ta hand om tänderna! I både Dubbel och Trippel veternärvård får du ersättning för den vanliga tandsjukdomen TR/FORL.',
        ];

  return (
    <View
      ref={widgetRef}
      className={'border-brand-trygg-hansa overflow-hidden rounded border-2 bg-white'}
      testID="pet-insurance-widget"
    >
      <View className={'md:flex-row'}>
        <View className={'flex-1 bg-white p-3'}>
          <Image
            src={insurancePartner.image}
            alt={insurancePartner.name}
            containerClassName={'aspect-3/1 mx-auto w-32 md:w-40'}
            imageClassName={'object-contain'}
            width={240}
          />

          <View className={'gap-1.5'}>
            {features.map((feature, index) => (
              <View key={index} className={'flex-row items-start gap-3'}>
                <View className={'bg-brand-green-light mt-1.5 rounded-full p-1'}>
                  <Icon icon={fasCheck} className={'text-brand-green-dark size-2.5'} />
                </View>

                <Text key={index} className={'text-brand-brown-dark flex-1 text-sm'}>
                  {feature}
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View className={'bg-brand-trygg-hansa flex-1 gap-3 p-3'}>
          <View
            className={
              'mx-auto max-w-full flex-row items-center justify-center gap-1.5 rounded bg-white p-1'
            }
          >
            <Image
              src={listing.media_assets[0] ?? listing.avatar}
              alt={listing.breed_name}
              containerClassName={'size-6 self-center'}
              imageClassName={'rounded object-cover'}
              sizeType={'avatar'}
            />

            <Text
              className={'text-brand-brown-dark flex-1 text-center font-semibold'}
              numberOfLines={1}
            >
              {listing.breed_name}
            </Text>
          </View>

          {!quote && !hasError && (
            <View className={'flex-1'}>
              <Loader light={true} dense={true} />
            </View>
          )}

          {hasError && (
            <Text className={'text-center text-sm text-white'}>
              Vi kunde inte ladda in priset för denna annons. Klicka på {'"'}Se ditt pris{'"'} för
              att beräkna priset själv.
            </Text>
          )}

          {quote && (
            <>
              <View className={'flex-1 justify-center'}>
                <Text className={'text-center text-sm text-white md:text-base'}>Pris från</Text>
                <Text className={'text-center text-2xl font-black text-white'}>
                  {quote.availableProductPackages[0]!.premiumInSekPerPaymentTerm.monthly} kr/mån
                </Text>
              </View>
            </>
          )}

          <View className={'gap-1.5'}>
            <BookInsuranceCallModal
              animal={listing.animal}
              widgetAnalyticsParams={widgetAnalyticsParams}
            />

            <PetInsurancePriceModal
              listing={listing}
              trigger={(modal) => (
                <Button
                  text={'Se ditt pris'}
                  icon={falAngleRight}
                  color={'brown'}
                  testID={'insurance-price-open'}
                  onPress={() => {
                    analytics.event('widget_see_price', widgetAnalyticsParams);
                    modal.open();
                  }}
                />
              )}
            />
          </View>
        </View>
      </View>
    </View>
  );
}
